import { createFileRoute } from "@tanstack/react-router";

import { Breadcrumb } from "~/components/ui/breadcrumb";
import { ButtonLink, ButtonRouter } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Wheel } from "~/components/wheel/wheel";
import { createBrandMeta } from "~/lib/shared/meta";
import { DEFAULT_WHEEL_LABELS } from "~/lib/shared/wheel";
import {
  buildWheelTemplateJsonLd,
  TEMPLATE_BLUF,
  TEMPLATE_CUSTOMIZING_ITEMS,
  TEMPLATE_FAQS,
  TEMPLATE_FORMAT_BLURBS,
  TEMPLATE_HOWTO_STEPS,
  TEMPLATE_PDF_BLANK,
  TEMPLATE_PDF_LABELED,
  TEMPLATE_SAMPLE_SCORES,
  TEMPLATE_SCORE_LEGEND,
} from "~/lib/wheel/template-content";

export const Route = createFileRoute("/_public/wheel-of-life/template")({
  staticData: { title: "Template" },
  head: () => ({
    ...createBrandMeta({
      title: "Wheel of Life Template — Free Printable PDF & Worksheet",
      description:
        "Free Wheel of Life template (Life Balance Wheel) — download a printable PDF worksheet or use the interactive version. Score 8 life areas in minutes. No signup.",
      ogTitle: "Wheel of Life Template — Free Printable PDF & Worksheet",
      ogDescription:
        "Free printable Wheel of Life worksheet — score 8 life areas, see where life is thriving and where it's stretched.",
      ogImage: "/og/wheel-of-life-generic.png",
      ogType: "article",
      twitterCard: "summary_large_image",
      canonical: "https://dreamcoachmatch.com/wheel-of-life/template",
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(buildWheelTemplateJsonLd()),
      },
    ],
  }),
  component: WheelOfLifeTemplate,
});

function WheelOfLifeTemplate() {
  return (
    <div className="bg-background">
      <main className="mx-auto max-w-5xl px-4 pb-24">
        {/* Hero */}
        <section className="pt-6 pb-8 md:pt-8">
          <Breadcrumb
            items={[
              { label: "Home", href: "/" },
              { label: "Wheel of Life", href: "/wheel-of-life" },
              { label: "Printable template" },
            ]}
            isBold={false}
          />
          <p className="mt-6 text-[13px] font-medium tracking-[0.08em] text-purple-700 uppercase">
            Free download
          </p>
          <h1 className="mt-3 text-4xl leading-[1.1] font-medium tracking-[-0.02em] text-gray-950 sm:text-5xl">
            Wheel of Life Template
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-gray-700">
            Free printable PDF, worksheet, and interactive version — score your
            8 life areas and see where life is thriving and where it's
            stretched.
          </p>
          <p className="mt-4 text-sm text-gray-500">
            By the{" "}
            <span className="font-medium text-gray-700">
              Coaching Intelligence Hub
            </span>{" "}
            at Dream Coach Match
          </p>
        </section>

        {/* Download cards */}
        <section className="grid gap-5 md:grid-cols-3">
          <Card className="border-purple-200 bg-gradient-to-b from-purple-50 to-card">
            <h2 className="text-xl font-medium text-gray-950">Printable PDF</h2>
            <p className="text-sm text-gray-700">
              A blank Wheel of Life worksheet with the 8 standard areas labeled,
              a scale, and room to score by hand. One page, ready to print. No
              email required.
            </p>
            <ButtonLink
              href={TEMPLATE_PDF_LABELED}
              download
              variant="primary"
              className="mt-auto w-fit"
            >
              Download PDF
            </ButtonLink>
          </Card>

          <Card>
            <h2 className="text-xl font-medium text-gray-950">
              Blank template
            </h2>
            <p className="text-sm text-gray-700">
              The same wheel with empty spokes — write your own life areas
              before you score. Best for coaches customizing it for a client, or
              anyone whose life doesn't fit the defaults.
            </p>
            <ButtonLink
              href={TEMPLATE_PDF_BLANK}
              download
              variant="default"
              className="mt-auto w-fit"
            >
              Download blank PDF
            </ButtonLink>
          </Card>

          <Card>
            <h2 className="text-xl font-medium text-gray-950">
              Use it interactively
            </h2>
            <p className="text-sm text-gray-700">
              Score on screen and get a personalized reflection plus your own
              shareable PDF — your wheel, your stretched areas, and your matched
              next steps.
            </p>
            <ButtonRouter
              to="/wheel-of-life"
              variant="primary"
              className="mt-auto w-fit"
            >
              Wheel of Life assessment
            </ButtonRouter>
          </Card>
        </section>

        {/* BLUF */}
        <section className="mx-auto mt-16 max-w-3xl">
          <p className="text-lg leading-relaxed text-gray-800">
            {TEMPLATE_BLUF}
          </p>
        </section>

        {/* How to use */}
        <section className="mx-auto mt-16 max-w-3xl">
          <p className="text-[13px] font-medium tracking-[0.08em] text-purple-700 uppercase">
            Four steps
          </p>
          <h2 className="mt-2 text-2xl font-medium tracking-tight text-gray-950 sm:text-3xl">
            How to use the template
          </h2>
          <ol className="mt-8 space-y-4">
            {TEMPLATE_HOWTO_STEPS.map((step, i) => (
              <li key={step.title}>
                <Card>
                  <div className="flex items-start gap-5">
                    <span className="grid size-10 shrink-0 place-items-center rounded-full bg-gradient-to-r from-[#6a62f2] to-[#a947ff] font-heading text-lg font-medium text-white">
                      {i + 1}
                    </span>
                    <div>
                      <h3 className="font-medium text-gray-950">
                        {step.title}
                      </h3>
                      <p className="mt-1 text-gray-700">{step.body}</p>
                    </div>
                  </div>
                </Card>
              </li>
            ))}
          </ol>
        </section>

        {/* Customizing */}
        <section className="mx-auto mt-16 max-w-3xl">
          <p className="text-[13px] font-medium tracking-[0.08em] text-purple-700 uppercase">
            For coaches and ongoing use
          </p>
          <h2 className="mt-2 text-2xl font-medium tracking-tight text-gray-950 sm:text-3xl">
            Customizing your wheel
          </h2>
          <div className="mt-8 space-y-4">
            {TEMPLATE_CUSTOMIZING_ITEMS.map((item) => (
              <Card key={item.title}>
                <h3 className="font-medium text-gray-950">{item.title}</h3>
                <p className="text-gray-700">{item.body}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* Diagram */}
        <section className="mt-16 grid items-center gap-10 md:grid-cols-2">
          <div className="flex justify-center">
            <Wheel
              mode="static"
              labels={[...DEFAULT_WHEEL_LABELS]}
              scores={[...TEMPLATE_SAMPLE_SCORES]}
              size={420}
            />
          </div>
          <div>
            <h2 className="text-2xl font-medium tracking-tight text-gray-950 sm:text-3xl">
              The Wheel of Life diagram
            </h2>
            <p className="mt-4 text-gray-700">
              The Wheel of Life diagram is a circle split into eight equal
              spokes, one per life area, with a scale running from the centre
              out to the rim. You mark your score on each spoke and join the
              marks, and the resulting chart shows your balance at a glance. The
              closer the shape sits to a full circle, the more even your
              satisfaction; the more jagged it looks, the clearer it is which
              areas need attention.
            </p>
            <dl className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
              {TEMPLATE_SCORE_LEGEND.map((item) => (
                <div key={item.score} className="flex items-baseline gap-1.5">
                  <dt className="font-medium text-purple-700">{item.score}</dt>
                  <dd className="text-sm text-gray-600">{item.label}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* Choose your format */}
        <section className="mx-auto mt-16 max-w-3xl">
          <p className="text-[13px] font-medium tracking-[0.08em] text-purple-700 uppercase">
            Two ways to use it
          </p>
          <h2 className="mt-2 text-2xl font-medium tracking-tight text-gray-950 sm:text-3xl">
            Choose your format
          </h2>
          <p className="mt-4 text-gray-700">
            You may also see this tool called the{" "}
            <strong className="font-medium">Life Balance Wheel</strong> or
            simply the Life Wheel — they are the same template under different
            names.
          </p>
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            {TEMPLATE_FORMAT_BLURBS.map((blurb) => (
              <Card key={blurb.title}>
                <h3 className="font-medium text-gray-950">{blurb.title}</h3>
                <p className="text-sm text-gray-700">{blurb.body}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mx-auto mt-16 max-w-3xl">
          <p className="text-[13px] font-medium tracking-[0.08em] text-purple-700 uppercase">
            Common questions
          </p>
          <h2 className="mt-2 text-2xl font-medium tracking-tight text-gray-950 sm:text-3xl">
            Wheel of Life template FAQ
          </h2>
          <dl className="mt-8 space-y-6">
            {TEMPLATE_FAQS.map((faq) => (
              <div key={faq.q}>
                <dt className="font-medium text-gray-950">{faq.q}</dt>
                <dd className="mt-1 leading-relaxed text-gray-700">{faq.a}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* CTA band */}
        <section className="mt-20">
          <div className="rounded-3xl bg-gray-950 px-8 py-12 text-center">
            <h2 className="text-2xl font-medium text-white sm:text-3xl">
              Take it as far as you want
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-gray-300">
              Start with the template today. When you want a sharper read or a
              partner to work through your wheel with, Dream Coach Match is
              here.
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-4">
              <ButtonRouter to="/wheel-of-life" variant="primary">
                Take the free Wheel of Life assessment →
              </ButtonRouter>
              <ButtonRouter
                to="/discovery"
                variant="ghost"
                className="border border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white"
              >
                Work with a coach who uses this →
              </ButtonRouter>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
